import { invalidate } from '@react-three/fiber';
import { type BufferGeometry, Matrix4 } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import {
    SettingModelSettingType,
    SolvePanelPlacementModelGapPositionPreferences,
    type SolverCostWeights
} from 'api/models';
import { InputContext } from 'app/modules/wallforming/components/input-binding/types/inputContext';
import { resolveInputContext } from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';
import { getInstanceLodLevel } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleLodUtils';
import {
    updateMultiPartInstanceEdges,
    updateSinglePartInstanceEdges
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { DEFAULT_DIMENSION_SETTINGS } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionSettings';
import { TransformationMode } from 'app/modules/wallforming/components/scenes/objects/helpers/types/transformationMode';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';

import type { UiSliceState } from 'app/modules/wallforming/components/state-management/placement/states/uiSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const defaultSolverCostWeights: SolverCostWeights = {
    instanceCount: 100,
    verticalOverbuild: 10,
    gapWidth: 1,
    gapPositionDeviation: 1,
    fillerUsage: 25,
    fillerWidthPenalty: 5,
    materialScarcity: 0,
    groupPreferenceBonus: -10,
    lockedPanelChangePenalty: 1000,
    price: 0,
    co2: 0,
    ppm: 2000
};

const defaultCornerSolverCostWeights: SolverCostWeights = {
    ...defaultSolverCostWeights,
    gapWidth: 0,
    gapPositionDeviation: 0,
    fillerUsage: 0,
    fillerWidthPenalty: 0,
    price: 20,
    co2: 20
};

export const createUiSlice: StateCreator<PlacementState, [], [], UiSliceState> = (set, get) => ({
    selectInputContext: (context) => {
        const state = get();
        if (resolveInputContext(state) === context) {
            state.setIsSpaceKeyPressed(false);
            return;
        }
        const isInsertionActive = () =>
            state.isAutoDragActiveRef.current ||
            state.isDragNewArticleStartRef.current ||
            state.isDragNewArticleActiveRef.current;
        if (isInsertionActive()) {
            state.discardArticleInstanceSelection(false);
            if (isInsertionActive()) {
                get().handleOnDragArticleEnd(true);
            }
        } else if (
            state.isDraggingActive() ||
            state.isTransformationActiveRef.current ||
            state.articleTransformationMode !== TransformationMode.NONE
        ) {
            state.cancelArticleTransformation();
        }
        get().cancelManualArticleInsertionDrag();
        if (state.isViewMode !== (context === InputContext.View)) {
            state.setIsViewMode(context === InputContext.View);
        }
        set({
            interactionMode: context === InputContext.Navigation ? InteractionMode.POI_NAVIGATION : InteractionMode.DEFAULT,
            isSpaceKeyPressed: false,
            arePivotControlsActive: context === InputContext.Transformation,
            isPanelQuickReplacementOpen: context === InputContext.Configuration,
            isPanelQuickReplacementGridActive: false,
            isPoiSelectionFeedbackVisible: false
        });
        invalidate();
    },

    showSceneStatusBarControls: true,
    setShowSceneStatusBarControls: (show) => set({ showSceneStatusBarControls: show }),

    showHeatmapOverlay: false,
    setShowHeatmapOverlay: (show) => set({ showHeatmapOverlay: show }),

    showHeatmapLabels: false,
    setShowHeatmapLabels: (show) => set({ showHeatmapLabels: show }),

    showHeatmapHover: false,
    setShowHeatmapHover: (show) => set({ showHeatmapHover: show }),

    heatmapMetric: 'overall',
    setHeatmapMetric: (metric) => set({ heatmapMetric: metric }),

    heatmapColorMap: 'classic',
    setHeatmapColorMap: (colorMap) => set({ heatmapColorMap: colorMap }),

    poiSelection: { current: { filterType: undefined, index: undefined, source: undefined } },
    setPoiSelection: async (filterType, index, source = PoiNavigationSource.PointerSelection) => {
        const commitPoiSelection = (clearData = true) => {
            const state = get();

            if (filterType === POIFilterType.Corner) {
                requestAnimationFrame(() => {
                    state.setFaceSelectionRef(null);
                    invalidate();
                });
            }

            state.runHistoryTransaction('historyPoiSelection', () => {
                set({
                    poiSelection: {
                        current: {
                            filterType,
                            index,
                            source,
                            shouldRealignCamera: !state.disableCameraAlignBySource[source]
                        }
                    },
                    isPoiSelectionFeedbackVisible:
                        index !== undefined &&
                        source === PoiNavigationSource.PointerSelection &&
                        state.interactionMode === InteractionMode.POI_NAVIGATION &&
                        !state.isSpaceKeyPressed
                });
            });
            if (clearData) {
                state.clearManualPlacementData();
            }
        };

        const state = get();
        if (state.arePivotControlsActive) {
            state.setArePivotControlsActive(false);
        }
        if (state.isPanelQuickReplacementOpen) {
            state.setIsPanelQuickReplacementOpen(false);
        }

        if (state.isAutoDragActiveRef.current) {
            const canRetarget = await state.updateAutoDragTarget(filterType, index);
            if (!canRetarget) {
                return;
            }

            commitPoiSelection(filterType !== POIFilterType.Corner);
            return;
        }

        commitPoiSelection();
    },

    workingPlane: null,
    setWorkingPlane: (plane) => set({ workingPlane: plane }),

    settingTypeFilter: undefined,
    setSettingTypeFilter: (settingType) => set({ settingTypeFilter: settingType }),

    selectedSettingView: SettingView.AUTOMATED_PLACEMENT,
    lastSelectedSettingView: SettingView.AUTOMATED_PLACEMENT,
    setSelectedSettingView: (settingView: SettingView | null) =>
        set(
            settingView === null
                ? { selectedSettingView: null }
                : {
                      selectedSettingView: settingView,
                      lastSelectedSettingView: settingView,
                      isViewMode: false
                  }
        ),

    selectedArticleId: null,
    setSelectedArticleId: (id) => {
        get().setInsertionPoint(0, 0, 0);
        set({
            selectedArticleId: id,
            manualPlacementData: {
                articleId: null,
                candidates: [],
                snapPoints: [],
                snapRanges: [],
                relatedPoiGuids: [],
                plane: null,
                poiIndex: undefined,
                isLoading: false,
                settingType: undefined
            }
        });
    },

    waitForPlacementData: false,
    isPlacementDataInitialized: false,
    setIsPlacementDataInitialized: (isInitialized: boolean) => set({ isPlacementDataInitialized: isInitialized }),

    manualPlacementData: {
        articleId: null,
        candidates: [],
        snapPoints: [],
        snapRanges: [],
        relatedPoiGuids: [],
        plane: null,
        poiIndex: undefined,
        isLoading: false,
        settingType: undefined
    },
    setManualPlacementData: (
        articleId,
        poiIndex,
        candidates,
        plane,
        settingType,
        snapPoints = [],
        snapRanges = [],
        relatedPoiGuids = []
    ) =>
        set({
            manualPlacementData: {
                articleId,
                poiIndex,
                candidates,
                snapPoints,
                snapRanges,
                relatedPoiGuids,
                plane,
                isLoading: false,
                settingType
            }
        }),

    setManualPlacementDataLoading: (articleId, isLoading) =>
        set({
            manualPlacementData: { ...get().manualPlacementData, articleId, isLoading }
        }),
    clearManualPlacementData: () =>
        set({
            manualPlacementData: {
                articleId: null,
                candidates: [],
                snapPoints: [],
                snapRanges: [],
                relatedPoiGuids: [],
                plane: null,
                poiIndex: undefined,
                isLoading: false,
                settingType: undefined
            }
        }),

    showInsertPointSelection: true,
    setShowInsertPointSelection: (show) => set({ showInsertPointSelection: show }),

    isPPMFilterActive: true,
    setIsPPMFilterActive: (isActive) => set({ isPPMFilterActive: isActive }),

    graphicsSettings: {
        shadowsEnabled: false,
        reflectionsEnabled: false,
        aoQuality: 'off',
        multisampling: 0,
        edgesOverlayEnabled: true,
        edgeLineThickness: 'medium',
        materialQuality: 'basic',
        showIfc: false,
        showFps: false
    },
    setGraphicsSettings: (settings) =>
        set((state) => ({ graphicsSettings: { ...state.graphicsSettings, ...settings } })),

    dimensionSettings: DEFAULT_DIMENSION_SETTINGS,
    setDimensionSettings: (settings) => {
        if (
            settings.projectionVerticalScale !== undefined &&
            (!Number.isFinite(settings.projectionVerticalScale) ||
                settings.projectionVerticalScale < 0.1 ||
                settings.projectionVerticalScale > 1)
        ) {
            throw new RangeError('Dimension vertical scale must be between 0.1 and 1.');
        }
        set((state) => ({
            dimensionSettings: {
                ...state.dimensionSettings,
                ...settings,
                chains: { ...state.dimensionSettings.chains, ...settings.chains }
            }
        }));
    },

    hiddenPOIFilterTypes: [],
    resetHiddenPOIFilterTypes: () => set({ hiddenPOIFilterTypes: [] }),
    addHiddenPOIFilterTypes: (filterType) =>
        set((state) => ({
            hiddenPOIFilterTypes: state.hiddenPOIFilterTypes.includes(filterType)
                ? state.hiddenPOIFilterTypes
                : [...state.hiddenPOIFilterTypes, filterType]
        })),
    removeHiddenPOIFilterTypes: (filterType) =>
        set((state) => ({
            hiddenPOIFilterTypes: state.hiddenPOIFilterTypes.filter((type) => type !== filterType)
        })),

    isArticleInsertionEnabled: () => {
        const state = get();
        return state.selectedArticleId !== null && state.poiSelection.current.filterType === POIFilterType.Wall;
    },

    articleEdgesOverlay: null,
    setArticleEdgesOverlay: (articleEdgesOverlay) => set({ articleEdgesOverlay }),

    updateArticleEdgesOverlay: (instanceLodOverrides) => {
        const state = get();
        if (state.isDraggingActive() && !state.isDragNewArticleActiveRef.current) {
            return;
        }

        const edgesArray: BufferGeometry[] = [];
        const tmpMatrix = new Matrix4();
        const camera = state.mainCameraControlsRef.current?.camera;
        const hiddenInstanceIds = state.hiddenInstanceIds;

        const articles = state.articles;
        Object.values(articles).forEach((article) => {
            const edgeGeometries = article.edgeGeometries;
            if (!edgeGeometries) {
                return;
            }

            const partGeometries = Object.entries(edgeGeometries);
            if (partGeometries.length === 0) {
                return;
            }

            if (partGeometries.length === 1) {
                updateSinglePartInstanceEdges(
                    {
                        ...article,
                        articleInstances: Object.fromEntries(
                            Object.entries(article.articleInstances).filter(
                                ([instanceId]) => !hiddenInstanceIds.has(instanceId)
                            )
                        )
                    },
                    edgesArray,
                    tmpMatrix,
                    (instanceId, instance) => {
                        const overrideLodLevel = instanceLodOverrides?.get(instanceId);
                        if (overrideLodLevel !== undefined) {
                            return overrideLodLevel;
                        }
                        const lodPart = Object.values(instance.parts)[0];
                        return lodPart ? getInstanceLodLevel(camera, lodPart.position, false) : 0;
                    }
                );
            } else {
                Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
                    if (hiddenInstanceIds.has(instanceId)) {
                        return;
                    }
                    const overrideLodLevel = instanceLodOverrides?.get(instanceId);
                    const lodPart = Object.values(instance.parts)[0];
                    const lodLevel =
                        overrideLodLevel !== undefined
                            ? overrideLodLevel
                            : lodPart
                              ? getInstanceLodLevel(camera, lodPart.position, false)
                              : 0;
                    updateMultiPartInstanceEdges(instanceId, instance, edgeGeometries, edgesArray, tmpMatrix, lodLevel);
                });
            }
        });

        if (edgesArray.length > 0) {
            const merged = mergeGeometries(edgesArray, false);
            get().setArticleEdgesOverlay(merged);
        } else {
            get().setArticleEdgesOverlay(null);
        }

        invalidate();
    },

    isContextMenuVisible: false,
    setIsContextMenuVisible: (isVisible) => set({ isContextMenuVisible: isVisible }),

    cameraFrustumViewerRef: { current: null },
    setCameraFrustumViewerRef: (ref) => set({ cameraFrustumViewerRef: { current: ref } }),

    cameraHelperData: null,
    setCameraHelperData: (data) => set({ cameraHelperData: data }),

    solverDebugInfo: null,
    setSolverDebugInfo: (info) => set({ solverDebugInfo: info }),

    placementSolutions: [],
    setPlacementSolutions: (solutions) =>
        set((state) => {
            const isSelectionEmpty = state.selectedPlacementSolution === null && solutions.length > 0;
            const isSelectionOutOfBounds =
                state.selectedPlacementSolution !== null && state.selectedPlacementSolution >= solutions.length;
            if (isSelectionEmpty || isSelectionOutOfBounds) {
                return { placementSolutions: solutions, selectedPlacementSolution: 0 };
            }
            return { placementSolutions: solutions };
        }),

    selectedPlacementSolution: null,
    setSelectedPlacementSolution: (index) => set({ selectedPlacementSolution: index }),

    appliedCornerPlacementSolutionByPoi: {},
    setAppliedCornerPlacementSolutionForPoi: (poiIndex, solutionKey) =>
        set((state) => ({
            appliedCornerPlacementSolutionByPoi: {
                ...state.appliedCornerPlacementSolutionByPoi,
                [poiIndex]: solutionKey
            }
        })),

    showPlacementPreview: true,
    setShowPlacementPreview: (show) => set({ showPlacementPreview: show }),

    solverUseForcedGapMode: false,
    setSolverUseForcedGapMode: (useForcedGapMode) => set({ solverUseForcedGapMode: useForcedGapMode }),

    solverForcedGapMode: SolvePanelPlacementModelGapPositionPreferences.AfterFirst,
    setSolverForcedGapMode: (forcedGapMode) => set({ solverForcedGapMode: forcedGapMode }),

    solverRequireVerticalGap: false,
    setSolverRequireVerticalGap: (requireVerticalGap) => set({ solverRequireVerticalGap: requireVerticalGap }),

    solverCostWeights: defaultSolverCostWeights,
    setSolverCostWeights: (weights) =>
        set((state) => ({
            solverCostWeights: {
                ...state.solverCostWeights,
                ...weights
            }
        })),

    automatedPlacementTab: PlacementTab.CATALOGUE,
    setAutomatedPlacementTab: (tab) => set({ automatedPlacementTab: tab }),

    selectedSettingType: SettingModelSettingType.Panels,
    setSelectedSettingType: (settingType) => set({ selectedSettingType: settingType }),

    automatedPlacementSettingsByType: {
        Panels: {
            requireVerticalGap: false,
            gapPositionPreferences: SolvePanelPlacementModelGapPositionPreferences.AfterFirst,
            solverCostWeights: defaultSolverCostWeights
        },
        Connectors: {
            solverCostWeights: defaultSolverCostWeights
        },
        Corners: {
            solverCostWeights: defaultCornerSolverCostWeights
        },
        StopEnd: {
            solverCostWeights: defaultSolverCostWeights
        }
    },
    autoPlaceSupportingConstructionClamps: false,
    setAutoPlaceSupportingConstructionClamps: (enabled) => set({ autoPlaceSupportingConstructionClamps: enabled }),
    setAutomatedPanelRequireVerticalGap: (requireVerticalGap) =>
        set((state) => ({
            automatedPlacementSettingsByType: {
                ...state.automatedPlacementSettingsByType,
                Panels: {
                    ...state.automatedPlacementSettingsByType.Panels,
                    requireVerticalGap
                }
            }
        })),
    setAutomatedPanelGapPositionPreferences: (mode) =>
        set((state) => ({
            automatedPlacementSettingsByType: {
                ...state.automatedPlacementSettingsByType,
                Panels: {
                    ...state.automatedPlacementSettingsByType.Panels,
                    gapPositionPreferences: mode
                }
            }
        })),
    setAutomatedSolverCostWeightsByType: (settingType, weights) =>
        set((state) => {
            const current = state.automatedPlacementSettingsByType;

            if (settingType === SettingModelSettingType.Panels) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Panels: {
                            ...current.Panels,
                            solverCostWeights: {
                                ...current.Panels.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            if (settingType === SettingModelSettingType.Corners) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Corners: {
                            ...current.Corners,
                            solverCostWeights: {
                                ...current.Corners.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            if (settingType === SettingModelSettingType.Connectors) {
                return {
                    automatedPlacementSettingsByType: {
                        ...current,
                        Connectors: {
                            ...current.Connectors,
                            solverCostWeights: {
                                ...current.Connectors.solverCostWeights,
                                ...weights
                            }
                        }
                    }
                };
            }

            return {
                automatedPlacementSettingsByType: {
                    ...current,
                    StopEnd: {
                        ...current.StopEnd,
                        solverCostWeights: {
                            ...current.StopEnd.solverCostWeights,
                            ...weights
                        }
                    }
                }
            };
        }),

    disableCameraAlignBySource: {
        [PoiNavigationSource.PointerSelection]: true,
        [PoiNavigationSource.PoiStepper]: true
    },
    setDisableCameraAlign: (source, disable) => {
        if (get().disableCameraAlignBySource[source] !== disable) {
            set((state) => ({
                disableCameraAlignBySource: { ...state.disableCameraAlignBySource, [source]: disable }
            }));
        }
    },

    interactionMode: InteractionMode.POI_NAVIGATION,
    isPoiSelectionFeedbackVisible: false,
    setInteractionMode: (mode) => {
        set({ interactionMode: mode, isPoiSelectionFeedbackVisible: false });
    },

    isSpaceKeyPressed: false,
    setIsSpaceKeyPressed: (isPressed) =>
        set((state) => ({
            isSpaceKeyPressed: isPressed,
            isPoiSelectionFeedbackVisible: !isPressed && state.isPoiSelectionFeedbackVisible
        })),

    isViewMode: false,
    setIsViewMode: (isViewMode) => {
        if (isViewMode) {
            const state = get();
            const isInsertionActive =
                state.isAutoDragActiveRef.current ||
                state.isDragNewArticleStartRef.current ||
                state.isDragNewArticleActiveRef.current;
            if (isInsertionActive) {
                state.discardArticleInstanceSelection(false);
                // Empty or not-yet-rendered insertions still need their drag session ended.
                if (
                    state.isAutoDragActiveRef.current ||
                    state.isDragNewArticleStartRef.current ||
                    state.isDragNewArticleActiveRef.current
                ) {
                    get().handleOnDragArticleEnd(true);
                }
            } else if (state.isDraggingActive() || state.articleTransformationMode !== TransformationMode.NONE) {
                state.cancelArticleTransformation();
            }
            get().cancelManualArticleInsertionDrag();
            get().setIsArticleDirectlySelectedRef(false);
            get().setIsZPositionUpdatePending(false);
            get().setArticleTransformationMode(TransformationMode.NONE);
            get().setIsPanelQuickReplacementOpen(false);
            get().setSelectedArticleId(null);
            set((current) => ({
                isViewMode,
                arePivotControlsActive: false,
                waitForPlacementData: false,
                lastSelectedSettingView: current.selectedSettingView ?? current.lastSelectedSettingView,
                selectedSettingView: null
            }));
        } else {
            set((state) => ({
                isViewMode,
                selectedSettingView: state.selectedSettingView ?? state.lastSelectedSettingView
            }));
        }
    }
});
